import React from 'react';
import DemographicsChart from './charts/DemographicsChart';
import PersonalValuesChart from './charts/PersonalValuesChart';
import BrandAffinityChart from './charts/BrandAffinityChart';
import DynamicComponentRenderer from './DynamicComponentRenderer';
import GenericSectionRenderer from './charts/GenericSectionRenderer';
import PsychologicalDriversChart from './charts/PsychologicalDriversChart';
import HobbiesHabitsChart from './charts/HobbiesHabitsChart';
import DailyRoutineChart from './charts/DailyRoutineChart';
import { ClientData, ClientSection, InsightIndexEntry, Audience } from '../types';

interface ClientResearchPageBetaProps {
  clientData: ClientData & {
    flexibleSections?: ClientSection[];
  };
  onRequestAdmin?: () => void;
  onAudienceChange?: (audienceId: string) => void;
}

export default function ClientResearchPageBeta({ clientData, onRequestAdmin, onAudienceChange }: ClientResearchPageBetaProps) {
  const audiences = clientData.audiences || [];
  const selectedAudienceId = clientData.currentAudienceId;

  const handleAudienceChange = (audienceId: string) => {
    if (onAudienceChange) {
      onAudienceChange(audienceId);
    }
  };

  React.useEffect(() => {
    const token = `Customer insights for ${clientData.name}`;
    document.title = token;
  }, [clientData?.name]);

  // Audience Tabs Component
  const renderAudienceTabs = () => {
    if (audiences.length <= 1) return null;

    return (
      <div className="bg-white border-b border-slate-200 sticky top-0 z-30 shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-2 py-3">
            <span className="text-sm font-medium text-slate-500 mr-3">Audience:</span>
            {audiences.map((audience) => (
              <button
                key={audience.id}
                onClick={() => handleAudienceChange(audience.id)}
                className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                  selectedAudienceId === audience.id
                    ? 'bg-teal-600 text-white shadow-md'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {audience.name}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const legacyDefaults = {
    demographics: true,
    personalValues: true,
    psychologicalDrivers: true,
    hobbiesAndHabits: true,
    dailyRoutine: true,
    brandAffinity: true,
  };
  const rawLegacy = clientData.uiSettings?.legacyCharts || {};
  const showLegacy = { ...legacyDefaults, ...rawLegacy };
  const resolveFlag = (flag: any): boolean => {
    if (flag === false || flag === 0) return false;
    if (flag === true || flag === 1) return true;
    if (typeof flag === 'number') {
      if (Number.isNaN(flag)) return true;
      return flag !== 0;
    }
    if (typeof flag === 'string') {
      const lowered = flag.trim().toLowerCase();
      if (lowered === 'false') return false;
      if (lowered === 'true') return true;
      if (lowered === '0') return false;
      if (lowered === '1') return true;
    }
    return true;
  };
  const shouldShow = (flag: any, hasData: boolean) => resolveFlag(flag) && hasData;
  const isSectionHidden = (title: string): boolean => {
    const sections = clientData.uiSettings?.sections || {} as Record<string, boolean>;
    const legacy = showLegacy as Record<string, any>;
    const normalize = (s: string) => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();
    const t = normalize(title);
    for (const [k, v] of Object.entries(sections)) {
      const nk = normalize(k);
      if (nk === t) return v === false;
      if ((nk.includes('site affinity') && t.includes('site affinity')) ||
          (nk.includes('category affinity') && t.includes('category affinity')) ||
          (nk.includes('audience') && (t.includes('audience') || t.includes('demographic highlights')))) {
        if (v === false) return true;
      }
    }
    if (t.includes('personal values') && !resolveFlag(legacy.personalValues)) return true;
    if (t.includes('psychological') && !resolveFlag(legacy.psychologicalDrivers)) return true;
    if ((t.includes('hobbies') || t.includes('habits')) && !resolveFlag(legacy.hobbiesAndHabits)) return true;
    if (t.includes('daily routine') && !resolveFlag(legacy.dailyRoutine)) return true;
    if (t.includes('brand affinity') && !resolveFlag(legacy.brandAffinity)) return true;
    if ((t.includes('audience') || t.includes('demographic highlights')) && !resolveFlag(legacy.demographics)) return true;
    return false;
  };
  const hasChartableData = (data: any): boolean => {
    if (!data) return false;
    if (Array.isArray(data)) {
      const f = data[0];
      if (f && typeof f === 'object' && (("label" in f || "name" in f) && ("value" in f || "index" in f || "composition" in f))) {
        return true;
      }
      if (f && typeof f === 'object') {
        const vals = Object.values(f as any);
        const hasString = vals.some(v => typeof v === 'string' && (v as string).trim() !== '');
        const hasNumber = vals.some(v => typeof v === 'number');
        if (hasString && hasNumber) return true;
      }
      if (f && typeof f === 'object' && 'title' in f && 'data' in f) {
        return (data as any[]).some((s: any) => hasChartableData(s.data));
      }
      return false;
    }
    if (typeof data === 'object') {
      return Object.values(data).some((v: any) => {
        if (typeof v === 'number') return true;
        if (typeof v === 'string') return /\d/.test(v);
        if (Array.isArray(v)) {
          const f = v[0];
          return !!(f && typeof f === 'object' && (("label" in f || "name" in f) && ("value" in f || "index" in f || "composition" in f)));
        }
        if (v && typeof v === 'object') {
          return Object.values(v).some((x: any) => typeof x === 'number' || (typeof x === 'string' && /\d/.test(x)));
        }
        return false;
      });
    }
    return false;
  };

  const getPriority = (title: string): number => {
    const t = (title || '').toLowerCase();
    if (t.includes('aeo/geo/seo') || (t.includes('aeo') && t.includes('seo'))) return -100;
    if (t.includes('audience')) return 0;
    if (t.includes('education') || t.includes('employment')) return 1;
    if (t.includes('personal values') || t.includes('psychological') || t.includes('hobbies') || t.includes('habits') || t.includes('daily routine')) return 2;
    if (t.includes('brand affinity')) return 3;
    if (
      t.includes('auto') || t.includes('car') ||
      t.includes('restaurant') || t.includes('food') ||
      t.includes('hotel') ||
      t.includes('airline') || t.includes('air ')
    ) return 4;
    return 5;
  };

  const getSecondaryPriority = (title: string): number => {
    const t = (title || '').toLowerCase();
    if (t.includes('education')) return 0;
    if (t.includes('employment')) return 1;
    if (t.includes('auto') || t.includes('car')) return 0;
    if (t.includes('restaurant') || t.includes('food')) return 1;
    if (t.includes('hotel')) return 2;
    if (t.includes('airline') || t.includes('air ')) return 3;
    return 9;
  };

  const aiSections = clientData.flexibleSections || [];
  const skipSectionTitles = new Set<string>();
  const normalizeTitle = (title: string) => String(title || '').toLowerCase().trim();
  const markSectionsAsSkipped = (patterns: string[]) => {
    if (!patterns || patterns.length === 0) return;
    aiSections.forEach((section) => {
      const titleNorm = normalizeTitle(section?.title || '');
      if (!titleNorm) return;
      if (patterns.some((pattern) => titleNorm.includes(pattern))) {
        skipSectionTitles.add(titleNorm);
      }
    });
  };

  const findFlexibleSectionByTitle = (patterns: string[]): ClientSection | undefined => {
    return aiSections.find((section) => {
      const titleNorm = normalizeTitle(section?.title || '');
      return patterns.some((pattern) => titleNorm.includes(pattern));
    });
  };

  const deriveInsightData = (primary: InsightIndexEntry[] | undefined, matcher: string) => {
    if (primary && primary.length > 0) return primary;
    return [];
  };

  const psychologicalSection = aiSections.find(sec => normalizeTitle(sec?.title || '').includes('psychological'));
  const hobbiesSection = aiSections.find(sec => normalizeTitle(sec?.title || '').includes('hobbies'));
  const routineSection = aiSections.find(sec => normalizeTitle(sec?.title || '').includes('daily routine'));
  const siteAffinitySection = aiSections.find(sec => {
    const titleNorm = normalizeTitle(sec?.title || '');
    return titleNorm.includes('site affinity') || titleNorm.includes('publisher');
  });
  const siteAffinityHasContent = !!(siteAffinitySection && (siteAffinitySection.componentCode || hasChartableData(siteAffinitySection.data)));
  const siteAffinityFlag = clientData.uiSettings?.sections?.['Site Affinity'];
  const showSiteAffinity = !!(
    siteAffinitySection &&
    !isSectionHidden(siteAffinitySection.title) &&
    shouldShow(siteAffinityFlag, siteAffinityHasContent)
  );

  const psychologicalDriversData = deriveInsightData(clientData.psychologicalDrivers, 'psychological');
  const hobbiesHabitsData = deriveInsightData(clientData.hobbiesAndHabits, 'hobbies');
  const dailyRoutineData = deriveInsightData(clientData.dailyRoutine, 'daily routine');

  const showPersonalValues = shouldShow(showLegacy.personalValues, (clientData.personalValues?.length ?? 0) > 0);
  const showPsychologicalDrivers = shouldShow(showLegacy.psychologicalDrivers !== false, (psychologicalDriversData.length > 0) || !!psychologicalSection);
  const showHobbiesHabits = shouldShow(showLegacy.hobbiesAndHabits !== false, (hobbiesHabitsData.length > 0) || !!hobbiesSection);
  const showDailyRoutine = shouldShow(showLegacy.dailyRoutine !== false, (dailyRoutineData.length > 0) || !!routineSection);
  const showBrandAffinity = shouldShow(showLegacy.brandAffinity, !!clientData.brandAffinity) && !!clientData.brandAffinity;
  const hasLifestyleSections = showPsychologicalDrivers || showHobbiesHabits || showDailyRoutine;
  const lifestyleHeaderTitle = showPsychologicalDrivers
    ? 'Mindset & Behavior'
    : showHobbiesHabits
      ? 'Hobbies & Habits'
      : showDailyRoutine
        ? 'Daily Routine'
        : null;
  const lifestyleHeaderIcon = showPsychologicalDrivers
    ? '🧠'
    : showHobbiesHabits
      ? '🎸'
      : showDailyRoutine
        ? '⏱️'
        : null;
  const isPersonalValuesSection = (title: string) => {
    const t = normalizeTitle(title);
    return t.includes('personal values');
  };
  const isLifestyleSection = (title: string) => {
    const t = normalizeTitle(title);
    return t.includes('psychological') || t.includes('hobbies') || t.includes('habits') || t.includes('daily routine');
  };

  const educationFlexSection = findFlexibleSectionByTitle(['education']);
  if (educationFlexSection) {
    skipSectionTitles.add(normalizeTitle(educationFlexSection.title));
  }

  const employmentFlexSection = findFlexibleSectionByTitle(['employment status']);
  if (employmentFlexSection) {
    skipSectionTitles.add(normalizeTitle(employmentFlexSection.title));
  }

  const renderSectionContent = (section: any): React.ReactNode => {
    if (!section) return null;
    if (isSectionHidden(section.title)) return null;
    const t = normalizeTitle(section.title);
    const forceGeneric = (
      t.includes('audience') ||
      t.includes('demographic highlights') ||
      t.includes('education') ||
      t.includes('employment') ||
      t.includes('brand affinity') ||
      t.includes('personal values') ||
      t.includes('psychological') ||
      t.includes('hobbies') || t.includes('habits') ||
      t.includes('daily routine') ||
      t.includes('site affinity') ||
      t.includes('category affinity') ||
      t.includes('aeo/geo/seo') || t.includes('aeo') || t.includes('geo') || t.includes('seo')
    );
    const mergedData = (section && section.data && typeof section.data === 'object' && !Array.isArray(section.data))
      ? { ...section.data, ...(section as any).metrics ? { metrics: (section as any).metrics } : {} }
      : section?.data;
    if (forceGeneric) {
      return (
        <GenericSectionRenderer
          title={section.title}
          data={mergedData}
          primaryColor={clientData.uiSettings?.theme?.primary}
          secondaryColor={clientData.uiSettings?.theme?.secondary}
        />
      );
    }
    if (section.componentCode) {
      return (
        <DynamicComponentRenderer
          componentCode={section.componentCode}
          data={section.data}
          props={section.componentProps || { title: section.title }}
        />
      );
    }
    return (
      <GenericSectionRenderer
        title={section.title}
        data={mergedData}
        primaryColor={clientData.uiSettings?.theme?.primary}
        secondaryColor={clientData.uiSettings?.theme?.secondary}
      />
    );
  };

  const demographicsSection =
    shouldShow(showLegacy.demographics, !!clientData.demographics) && clientData.demographics ? (
      <DemographicsChart
        key="demographics"
        data={clientData.demographics}
        clientId={clientData.id}
      />
    ) : null;
  if (demographicsSection) {
    markSectionsAsSkipped(['audience', 'demographic highlights', 'demographics']);
  }

  const psychologicalSectionNode = (() => {
    if (!(showPersonalValues || showPsychologicalDrivers || showHobbiesHabits || showDailyRoutine)) return null;
    if (showPersonalValues) {
      markSectionsAsSkipped(['personal values']);
    }
    return (
      <div key="psychological" className="space-y-8">
        {showPersonalValues && (
          <PersonalValuesChart
            values={clientData.personalValues}
            primaryColor={clientData.uiSettings?.theme?.primary}
            topWriteUps={clientData.uiSettings?.personalValues?.topWriteUps || []}
          />
        )}
        {(showPsychologicalDrivers || showHobbiesHabits || showDailyRoutine) && (
          <div className="space-y-6">
            {hasLifestyleSections && lifestyleHeaderTitle ? (
              <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-start gap-3">
                    {lifestyleHeaderIcon ? (
                      <span className="text-3xl leading-none mt-0.5">{lifestyleHeaderIcon}</span>
                    ) : null}
                    <div>
                      <h2 className="text-2xl font-bold text-slate-900">{lifestyleHeaderTitle}</h2>
                      <p className="text-slate-600">
                        BASELINE: Online Adult Population | CONTAINS: Engaged Audiences Data
                      </p>
                    </div>
                  </div>
                  <div className="text-[11px] uppercase tracking-wide text-slate-500">
                    BY INDEX with minimum composition: 5%
                  </div>
                </div>
              </div>
            ) : null}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {showPsychologicalDrivers && (
                psychologicalDriversData.length > 0 ? (
                  <PsychologicalDriversChart
                    key="psychological-insights"
                    data={psychologicalDriversData}
                    primaryColor={clientData.uiSettings?.theme?.primary}
                    secondaryColor={clientData.uiSettings?.theme?.secondary}
                  />
                ) : psychologicalSection ? (
                  (() => {
                    skipSectionTitles.add(normalizeTitle(psychologicalSection.title));
                    return (
                      <GenericSectionRenderer
                        title={psychologicalSection.title}
                        data={psychologicalSection.data}
                        primaryColor={clientData.uiSettings?.theme?.primary}
                        secondaryColor={clientData.uiSettings?.theme?.secondary}
                      />
                    );
                  })()
                ) : null
              )}
              {showHobbiesHabits && (
                hobbiesHabitsData.length > 0 ? (
                  <HobbiesHabitsChart
                    key="hobbies-insights"
                    data={hobbiesHabitsData}
                    primaryColor={clientData.uiSettings?.theme?.primary}
                    secondaryColor={clientData.uiSettings?.theme?.secondary}
                  />
                ) : hobbiesSection ? (
                  (() => {
                    skipSectionTitles.add(normalizeTitle(hobbiesSection.title));
                    return (
                      <GenericSectionRenderer
                        title={hobbiesSection.title}
                        data={hobbiesSection.data}
                        primaryColor={clientData.uiSettings?.theme?.primary}
                        secondaryColor={clientData.uiSettings?.theme?.secondary}
                      />
                    );
                  })()
                ) : null
              )}
              {showDailyRoutine && (
                dailyRoutineData.length > 0 ? (
                  <div className="lg:col-span-2">
                    <DailyRoutineChart
                      key="routine-insights"
                      data={dailyRoutineData}
                      primaryColor={clientData.uiSettings?.theme?.primary}
                      secondaryColor={clientData.uiSettings?.theme?.secondary}
                    />
                  </div>
                ) : routineSection ? (
                  (() => {
                    skipSectionTitles.add(normalizeTitle(routineSection.title));
                    return (
                      <div className="lg:col-span-2">
                        <GenericSectionRenderer
                          title={routineSection.title}
                          data={routineSection.data}
                          primaryColor={clientData.uiSettings?.theme?.primary}
                          secondaryColor={clientData.uiSettings?.theme?.secondary}
                        />
                      </div>
                    );
                  })()
                ) : null
              )}
            </div>
          </div>
        )}
      </div>
    );
  })();

  const brandAffinitySection = showBrandAffinity && clientData.brandAffinity ? (() => {
    markSectionsAsSkipped(['brand affinity', 'retailer', 'restaurant', 'hotel', 'airline', 'auto', 'car']);
    return (
      <BrandAffinityChart
        key="brand-affinity"
        data={clientData.brandAffinity}
        primaryColor={clientData.uiSettings?.theme?.primary}
      />
    );
  })() : null;

  const siteAffinitySectionNode = showSiteAffinity && siteAffinitySection ? (() => {
    markSectionsAsSkipped(['site affinity', 'publisher']);
    const mergedData = (siteAffinitySection && siteAffinitySection.data && typeof siteAffinitySection.data === 'object' && !Array.isArray(siteAffinitySection.data))
      ? { ...siteAffinitySection.data, ...(siteAffinitySection as any).metrics ? { metrics: (siteAffinitySection as any).metrics } : {} }
      : siteAffinitySection?.data;
    if (siteAffinitySection.componentCode) {
      return (
        <DynamicComponentRenderer
          key="site-affinity"
          componentCode={siteAffinitySection.componentCode}
          data={siteAffinitySection.data}
          props={siteAffinitySection.componentProps || { title: siteAffinitySection.title }}
        />
      );
    }
    return (
      <GenericSectionRenderer
        key="site-affinity"
        title={siteAffinitySection.title}
        data={mergedData}
        primaryColor={clientData.uiSettings?.theme?.primary}
        secondaryColor={clientData.uiSettings?.theme?.secondary}
      />
    );
  })() : null;

  const orderedSections: React.ReactElement[] = [];
  if (demographicsSection) orderedSections.push(demographicsSection);
  if (psychologicalSectionNode) orderedSections.push(psychologicalSectionNode);
  if (brandAffinitySection) orderedSections.push(brandAffinitySection as React.ReactElement);
  if (siteAffinitySectionNode) orderedSections.push(siteAffinitySectionNode as React.ReactElement);

  const theme = clientData.uiSettings?.theme;
  const heroContent = clientData.pageContent?.research?.hero;
  const heroStyle = heroContent?.backgroundImage
    ? {
        backgroundImage: `linear-gradient(120deg, rgba(15,23,42,0.78), rgba(30,41,59,0.68)), url(${heroContent.backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    : {
        background: `linear-gradient(135deg, ${theme?.primary || '#14B8A6'}, rgba(14,116,144,0.95))`,
      };

  // Get current audience name for display
  const currentAudience = audiences.find(a => a.id === selectedAudienceId);
  const audienceLabel = currentAudience?.name || 'Primary';

  return (
    <div className="bg-slate-100 min-h-screen flex flex-col">
      {/* Audience Tabs - Sticky at top */}
      {renderAudienceTabs()}

      <section className="relative overflow-hidden">
        <div className="min-h-[24vh] md:min-h-[28vh] flex items-center py-5" style={heroStyle}>
          <div className="container mx-auto px-6">
            {heroContent?.eyebrow && (
              <div className="text-xs uppercase tracking-[0.4em] text-white/70 mb-3">
                {heroContent.eyebrow}
              </div>
            )}
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
              {heroContent?.title || 'Research Insights'}
              {audiences.length > 1 && (
                <span className="ml-3 text-lg md:text-xl font-normal text-white/70">
                  — {audienceLabel}
                </span>
              )}
            </h1>
            <p className="text-white/85 text-base md:text-lg max-w-3xl leading-relaxed">
              {heroContent?.subtitle || clientData.headline}
            </p>
            {heroContent?.description && (
              <p className="text-white/75 text-sm md:text-base mt-4 max-w-3xl leading-relaxed">
                {heroContent.description}
              </p>
            )}
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-10 flex-1">
        {(() => {
          const intro = clientData.pageContent?.research?.intro;
          if (!intro?.body && !intro?.title) return null;
          return (
            <div className="bg-white rounded-xl shadow-md border border-slate-100 p-6 mb-10">
              {intro?.title && <h2 className="text-2xl font-semibold text-slate-900 mb-3">{intro.title}</h2>}
              {intro?.body && (
                <div
                  className="prose prose-slate max-w-none"
                  dangerouslySetInnerHTML={{ __html: intro.body }}
                />
              )}
              {(intro?.ctaLabel && intro?.ctaUrl) && (
                <a
                  href={intro.ctaUrl}
                  className="inline-flex text-sm font-semibold items-center mt-4"
                  style={{ color: clientData.uiSettings?.theme?.primary || '#14B8A6' }}
                >
                  {intro.ctaLabel}
                  <svg className="ml-2 h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7" />
                  </svg>
                </a>
              )}
            </div>
          );
        })()}

        {/* Charts Section */}
        <div className="space-y-12">
          {orderedSections.map((section, idx) => (
            <React.Fragment key={(section.key as string | number | null) ?? idx}>
              {section}
            </React.Fragment>
          ))}

          {/* Flexible AI-Generated Sections */}
          {aiSections.length > 0 && (() => {
            const filtered = aiSections
              .filter((s) => {
                const t = normalizeTitle(s.title);
                if (t.includes('category affinity')) return false;
                const hasAudience = aiSections.some(sec => {
                  const tt = normalizeTitle(sec.title);
                  return tt.includes('audience') || tt.includes('demographic highlights');
                });
                if (!hasAudience) return true;
                if (t.includes('education')) return false;
                if (t.includes('employment')) return false;
                if (t.includes('household income')) return false;
                if (t.includes('marital')) return false;
                if (t.includes('children')) return false;
                return true;
              })
              .sort((a, b) => {
                const pa = getPriority(a.title);
                const pb = getPriority(b.title);
                if (pa !== pb) return pa - pb;
                const sa = getSecondaryPriority(a.title);
                const sb = getSecondaryPriority(b.title);
                if (sa !== sb) return sa - sb;
                if (typeof a.order === 'number' && typeof b.order === 'number') return a.order - b.order;
                return (a.title || '').localeCompare(b.title || '');
              });
            const personalItems: Array<{ key: string | number; node: React.ReactNode }> = [];
            const lifestyleItems: Array<{ key: string | number; node: React.ReactNode }> = [];
            const otherItems: Array<{ key: string | number; node: React.ReactNode }> = [];
            filtered.forEach((section, index) => {
              const content = renderSectionContent(section);
              if (!content) return;
              const titleNorm = normalizeTitle(section.title);
              if (skipSectionTitles.has(titleNorm)) return;
              if (titleNorm.includes('aeo/geo/seo') || (titleNorm.includes('aeo') && titleNorm.includes('seo')) || titleNorm.includes('current state')) {
                return;
              }
              if (isPersonalValuesSection(section.title)) {
                personalItems.push({ key: section.id || `personal-${index}`, node: content });
                return;
              }
              if (isLifestyleSection(section.title)) {
                return;
              }
              otherItems.push({ key: section.id || index, node: content });
            });
            return (
              <div className="space-y-8">
                {personalItems.map(item => (
                  <div key={item.key} className="w-full">
                    {item.node}
                  </div>
                ))}
                {lifestyleItems.length > 0 && (
                  <div className={`grid grid-cols-1 ${lifestyleItems.length > 1 ? 'lg:grid-cols-2' : ''} gap-8`}>
                    {lifestyleItems.map((item, idx) => {
                      const isLastOdd = lifestyleItems.length % 2 === 1 && idx === lifestyleItems.length - 1;
                      return (
                        <div key={item.key} className={isLastOdd && lifestyleItems.length > 1 ? 'lg:col-span-2' : ''}>
                          {item.node}
                        </div>
                      );
                    })}
                  </div>
                )}
                {otherItems.map(item => (
                  <div key={item.key} className="w-full">
                    {item.node}
                  </div>
                ))}
              </div>
            );
          })()}
        </div>

        {/* Indexing explainer */}
        <div className="mt-10 text-center text-slate-600 text-sm max-w-3xl mx-auto">
          Data indexing for customer audience segments measures how much more or less likely a specific group is to do something compared to the average person.
        </div>

      </div>
      <footer className="bg-slate-900/80 border-t border-white/10">
        <div className="container mx-auto px-6 py-12 text-center text-white/70 text-sm">
          <img src="https://lhijeqvswxugvtfwkoyl.supabase.co/storage/v1/object/public/client-files/littlefield-logo.png" alt="Littlefield Agency" className="w-[200px] h-auto object-contain mx-auto mb-2" />
          <p>Powered by Littlefield Agency | Customer insights for {clientData.name}</p>
        </div>
      </footer>
    </div>
  );
}
